import React, { useState, useEffect } from 'react';
import { useCarouselData, useBasicInfo } from '../data/menuData';
import chefImage from '../assets/chef.svg';

const HomeSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slideDirection, setSlideDirection] = useState('next');
  const { carouselSlides, loading } = useCarouselData();
  const { basicInfo } = useBasicInfo();

  useEffect(() => {
    if (carouselSlides.length === 0) return;
    
    const interval = setInterval(() => {
      if (!isTransitioning) {
        setSlideDirection('next');
        setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
      }
    }, 2500);

    return () => clearInterval(interval);
  }, [isTransitioning, carouselSlides.length]);

  const nextSlide = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setSlideDirection('next');
      setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
      setTimeout(() => setIsTransitioning(false), 600);
    }
  };

  const prevSlide = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setSlideDirection('prev');
      setCurrentSlide((prev) => (prev - 1 + carouselSlides.length) % carouselSlides.length);
      setTimeout(() => setIsTransitioning(false), 600);
    }
  };

  const goToSlide = (index) => {
    if (!isTransitioning && index !== currentSlide) {
      setIsTransitioning(true);
      setSlideDirection(index > currentSlide ? 'next' : 'prev');
      setCurrentSlide(index);
      setTimeout(() => setIsTransitioning(false), 600);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="home" className="min-h-screen bg-gradient-to-br from-[#F8E8D2]/20 via-white to-[#F8E8D2]/20 relative overflow-hidden pt-16 sm:pt-18 md:pt-20 lg:pt-22 xl:pt-24">
      <div className="absolute inset-0 bg-gradient-to-br from-[#F8E8D2]/10 via-white/5 to-[#F8E8D2]/10"></div>
      
      <div className="max-w-6xl mx-auto px-0 sm:px-5 relative z-10 h-full flex items-center">
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center py-4 sm:py-6 lg:py-8 w-full">
          {/* Hero Text */}
          <div className="text-center lg:text-left space-y-4 sm:space-y-6 animate-slide-in lg:order-1 order-2 px-5 sm:px-0">
            <h1 className="text-4xl md:text-5xl font-segoe font-bold text-gray-800 leading-tight">
              Welcome to Vanilla Sky Bakehouse
            </h1>
            
            <p className="text-base text-xl font-segoeNormal text-gray-600 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Experience the magic of freshly baked delights, where every cheesecake and cake is crafted with love, tradition, and a touch of modern artistry. Each bite tells a story of passion, sweetness, and timeless indulgence.
            </p>
            

            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
              <button 
                onClick={() => scrollToSection('menu')}
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 border-2 border-primary text-gray-800 font-semibold rounded-full hover:bg-primary hover:text-black transition-all duration-300 text-sm sm:text-base"
              >
                <i className="fas fa-utensils"></i>
                Explore Our Menu
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 border-2 border-primary text-gray-800 font-semibold rounded-full hover:bg-primary hover:text-black transition-all duration-300 text-sm sm:text-base"
              >
                <i className="fas fa-phone"></i>
                Contact Us
              </button>
            </div>
          </div>
          
          {/* Hero Image/Carousel */}
          <div className="flex justify-center lg:justify-end lg:order-2 order-1">
            <div className="relative w-full h-74 sm:h-72 md:h-80 lg:w-96 lg:h-96 xl:w-[420px] xl:h-[420px] lg:rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl bg-white">
              {carouselSlides.length > 0 ? (
                <>
                  <div 
                    className="carousel-container w-full h-full relative overflow-hidden cursor-pointer"
                    onClick={(e) => {
                      // Only trigger if clicking on the carousel image area, not on controls
                      if (!e.target.closest('.carousel-btn') && !e.target.closest('.dot')) {
                        scrollToSection('menu');
                      }
                    }}
                  >
                    {carouselSlides.map((slide, index) => {
                      const isActive = index === currentSlide;
                      const isPrev = index === (currentSlide - 1 + carouselSlides.length) % carouselSlides.length;
                      const isNext = index === (currentSlide + 1) % carouselSlides.length;
                      
                      return (
                        <div 
                          key={`slide-${slide.id}-${index}`}
                          className={`carousel-slide absolute inset-0 ${
                            isActive 
                              ? 'active z-10' 
                              : isPrev
                              ? 'prev z-5'
                              : isNext
                              ? 'next z-5'
                              : 'hidden'
                          }`}
                          style={{
                            animation: isActive 
                              ? slideDirection === 'next' 
                                ? 'slideInFromRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)' 
                                : 'slideInFromLeft 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                              : isPrev
                              ? slideDirection === 'next'
                                ? 'slideOutToLeft 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                                : 'slideOutToRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                              : 'none'
                          }}
                        >
                          <img src={slide.imageUrl} alt={slide.title} className="w-full h-full object-cover" />
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent text-white p-3 sm:p-4 md:p-6">
                            <h3 className="font-dm-sans font-bold text-sm sm:text-base md:text-xl mb-1 sm:mb-2">{slide.title}</h3>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Carousel Controls */}
                  <div className="absolute inset-0 flex items-center justify-between px-2 sm:px-4 z-10 pointer-events-none">
                    <button 
                      onClick={prevSlide}
                      className="carousel-btn prev w-10 h-10 sm:w-12 sm:h-12 bg-white/90 rounded-full flex items-center justify-center text-gray-700 hover:bg-primary hover:text-white transition-all duration-300 shadow-lg pointer-events-auto"
                    >
                      <i className="fas fa-chevron-left text-lg sm:text-xl"></i>
                    </button>
                    
                    <button 
                      onClick={nextSlide}
                      className="carousel-btn next w-10 h-10 sm:w-12 sm:h-12 bg-white/90 rounded-full flex items-center justify-center text-gray-700 hover:bg-primary hover:text-white transition-all duration-300 shadow-lg pointer-events-auto"
                    >
                      <i className="fas fa-chevron-right text-lg sm:text-xl"></i>
                    </button>
                  </div>
                  
                  {/* Carousel Dots */}
                  <div className="absolute bottom-4 sm:bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2 sm:gap-3 z-10">
                    {carouselSlides.map((_, index) => (
                      <span 
                        key={index}
                        className={`dot w-3 h-3 sm:w-4 sm:h-4 bg-white/50 rounded-full cursor-pointer transition-all duration-300 ${index === currentSlide ? 'active bg-primary scale-125' : ''}`}
                        onClick={() => goToSlide(index)}
                      ></span>
                    ))}
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <div className="text-center text-gray-500">
                    <i className="fas fa-spinner fa-spin text-2xl mb-2"></i>
                    <p>Loading carousel...</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeSection; 